import { Box, Button, Card, Grid, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Edit } from '@mui/icons-material';
import Loader from '@shared/components/Loader';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { WeeklySchedule, DaySchedule, Holiday } from '../entity';
import { useWorkScheduleQuery } from '../hooks';

type MonthlyWorkScheduleProps = {
  currentDate: Dayjs;
  onDateChange: (date: Dayjs) => void;
  onDayClick: (date: Dayjs) => void;
  onWeekdayEdit: (weekday: keyof WeeklySchedule) => void;
};

const MonthlyWorkSchedule = ({
  currentDate,
  onDateChange,
  onDayClick,
  onWeekdayEdit,
}: MonthlyWorkScheduleProps) => {
  const intl = useIntl();
  const year = currentDate.year();
  const month = currentDate.month() + 1; // dayjs months are 0-indexed

  const { data: workSchedule, isLoading, error } = useWorkScheduleQuery(year, month);

  const handlePreviousMonth = () => {
    onDateChange(currentDate.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    onDateChange(currentDate.add(1, 'month'));
  };

  const handleToday = () => {
    onDateChange(dayjs());
  };

  if (isLoading) {
    return <Loader />;
  }

  const daysInMonth = currentDate.daysInMonth();
  const monthName = currentDate.format('MMMM YYYY');

  const weeklySchedule = workSchedule?.schedule?.weeklySchedule;
  const holidays = workSchedule?.schedule?.holidays || [];
  const dayOverrides = workSchedule?.schedule?.dayOverrides || {};

  // Helper to get English day name (WeeklySchedule keys are always in English)
  const getEnglishDayName = (date: Dayjs): keyof WeeklySchedule => {
    const dayMap: Record<number, keyof WeeklySchedule> = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday',
    };
    return dayMap[date.day()];
  };

  const getDayScheduleForDate = (
    day: number,
  ): { daySchedule: DaySchedule; holiday?: Holiday; isOverride?: boolean } => {
    const date = currentDate.date(day);
    const dateStr = date.format('YYYY-MM-DD');

    const override = dayOverrides[dateStr];
    if (override) {
      return { daySchedule: override, isOverride: true };
    }

    // Get the day of week schedule
    const dayOfWeek = getEnglishDayName(date);
    const daySchedule = weeklySchedule?.[dayOfWeek] || { isWorking: false, timeSlots: [] };

    // Check if this date has a holiday
    const holiday = holidays.find(h => h.date === dateStr);

    return { daySchedule, holiday };
  };

  const renderDayCard = (day: number) => {
    const date = currentDate.date(day);
    const dayName = date.format('ddd');
    const isToday = date.isSame(dayjs(), 'day');
    const isWeekend = date.day() === 0 || date.day() === 6;

    const { daySchedule, holiday, isOverride } = getDayScheduleForDate(day);

    return (
      <Grid size={1} key={day}>
        <Card
          onClick={() => onDayClick(date)}
          sx={{
            p: 0.5,
            cursor: 'pointer',
            border: isToday ? '2px solid' : '1px solid',
            borderColor: isToday ? 'primary.main' : isOverride ? 'warning.main' : 'divider',
            bgcolor: !daySchedule.isWorking
              ? 'grey.200'
              : isWeekend
                ? 'grey.100'
                : 'background.paper',
            '&:hover': {
              boxShadow: 2,
            },
          }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                {day}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dayName}
              </Typography>
            </Stack>
            {isOverride && (
              <Typography variant="body2" fontSize={9} color="warning.dark" fontWeight="bold">
                <FormattedMessage id="workSchedule.customDay" defaultMessage="Upraveno" />
              </Typography>
            )}
            {!daySchedule.isWorking ? (
              <Typography variant="body2" color="text.disabled" fontSize={11}>
                <FormattedMessage id="workSchedule.nonWorking" defaultMessage="Volno" />
              </Typography>
            ) : (
              <>
                {daySchedule.timeSlots.map((slot, idx) => (
                  <Typography key={idx} variant="body2" fontSize={11}>
                    {slot.start} - {slot.end}
                  </Typography>
                ))}
                {holiday && (
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                    {holiday.name && (
                      <Typography variant="body2" color="error" fontSize={10} fontWeight="bold">
                        {holiday.name}
                      </Typography>
                    )}
                    {holiday.startTime && holiday.endTime ? (
                      <Typography variant="body2" color="error" fontSize={10}>
                        {holiday.startTime} - {holiday.endTime}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="error" fontSize={10}>
                        <FormattedMessage
                          id="workSchedule.fullDayHoliday"
                          defaultMessage="Celý den"
                        />
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mb={3}>
        <IconButton onClick={handlePreviousMonth}>
          <ChevronLeft />
        </IconButton>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            {monthName}
          </Typography>
          <Button variant="outlined" size="small" onClick={handleToday}>
            <FormattedMessage id="workSchedule.today" defaultMessage="Dnes" />
          </Button>
        </Stack>
        <IconButton onClick={handleNextMonth}>
          <ChevronRight />
        </IconButton>
      </Stack>

      {error && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          <FormattedMessage
            id="workSchedule.noData"
            defaultMessage="Zatím nebyly nastaveny žádné pracovní hodiny. Klikněte na den pro nastavení."
          />
        </Typography>
      )}

      {/* Week header with edit buttons */}
      <Box sx={{ mb: 2 }} id="profile-working-schedule-weekly-schedule">
        <Grid container spacing={1} columns={7}>
          {(
            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
          ).map(weekday => {
            const daySchedule = weeklySchedule?.[weekday];
            const dayLabel = intl.formatMessage({
              id: `weekday.${weekday}`,
              defaultMessage: weekday.charAt(0).toUpperCase() + weekday.slice(1),
            });

            return (
              <Grid size={1} key={weekday}>
                <Card
                  sx={{
                    p: 1,
                    bgcolor: 'primary.light',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.main' },
                  }}
                  onClick={() => onWeekdayEdit(weekday)}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" fontWeight="bold">
                        {dayLabel.substring(0, 3)}
                      </Typography>
                      <Edit sx={{ fontSize: 12 }} />
                    </Stack>
                    {daySchedule?.isWorking ? (
                      <Typography variant="caption" fontSize={9}>
                        {daySchedule.timeSlots.map(slot => `${slot.start}-${slot.end}`).join(', ')}
                      </Typography>
                    ) : (
                      <Typography variant="caption" fontSize={9} color="text.disabled">
                        <FormattedMessage id="workSchedule.nonWorking" defaultMessage="Volno" />
                      </Typography>
                    )}
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Grid container spacing={1} columns={7} id="profile-working-schedule-holiday">
        {Array.from(
          {
            length: (() => {
              const d = currentDate.date(1).day();
              return d === 0 ? 6 : d - 1;
            })(),
          },
          (_, i) => (
            <Grid size={1} key={`empty-${i}`} />
          ),
        )}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(renderDayCard)}
      </Grid>
    </Box>
  );
};

export default MonthlyWorkSchedule;
